"use strict";
/*!
 * Copyright 2012 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.JsbiSupport = void 0;
const jsbi_1 = __importDefault(require("jsbi"));
/**
 * This class provides helper methods and static values for working with JSBI integers.
 */
class JsbiSupport {
    /**
     * Indicates whether the provided value is zero without allocating a new JSBI value to represent zero.
     * @param value The integer to test.
     * @return  True if the value is equal to zero.
     */
    static isZero(value) {
        return jsbi_1.default.equal(value, JsbiSupport.ZERO);
    }
    /**
     * Indicates whether the provided value is less than zero without allocating a new JSBI value to represent zero.
     * @param value
     */
    static isNegative(value) {
        return jsbi_1.default.lessThan(value, JsbiSupport.ZERO);
    }
    /**
     * Creates a JSBI value from the given string.
     *
     * The static JSBI.BigInt() constructor method can parse hex, decimal, octal, and binary values without issue based
     * on their prefixes ('0x', '', '0o', and '0b' respectively), it will only parse values that are negative if the
     * notation is in decimal. For example, it will successfully parse "14", "-14", and "0x0e", but will throw an
     * Error when asked to parse "-0x0e". This limitation is part of the BigInt spec.
     */
    static bigIntFromString(text) {
        let isNegative = false;
        let magnitudeText = text.trimLeft();
        if (text.startsWith("-")) {
            isNegative = true;
            magnitudeText = text.substring(1);
        }
        let bigInt = jsbi_1.default.BigInt(magnitudeText);
        if (isNegative) {
            bigInt = jsbi_1.default.unaryMinus(bigInt);
        }
        return bigInt;
    }
    /**
     * By default, converting a BigInt to a Number causes precision loss for values outside of the range
     * [Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER]. This function will clamp the given value to a number
     * within that range.
     *
     * @param value The BigInt to convert to a number, possibly clamping it in the process.
     * @return  Number.MIN_SAFE_INTEGER if `value` is less than Number.MIN_SAFE_INTEGER,
     *          Number.MAX_SAFE_INTEGER if `value` is greater than Number.MAX_SAFE_INTEGER,
     *          or the Number representation of `value` if it is already within that range.
     */
    static clampToSafeIntegerRange(value) {
        if (jsbi_1.default.greaterThan(value, this.NUMBER_MAX_SAFE_INTEGER)) {
            return Number.MAX_SAFE_INTEGER;
        }
        if (jsbi_1.default.lessThan(value, this.NUMBER_MIN_SAFE_INTEGER)) {
            return Number.MIN_SAFE_INTEGER;
        }
        return jsbi_1.default.toNumber(value);
    }
    /**
     * Indicates whether the provided JSBI value is within the range[ Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER].
     *
     * @param value     The integer to test.
     * @return          True if JSBI.toNumber(value) can be called without losing any precision.
     */
    static isSafeInteger(value) {
        return (jsbi_1.default.greaterThanOrEqual(value, this.NUMBER_MIN_SAFE_INTEGER) &&
            jsbi_1.default.lessThanOrEqual(value, this.NUMBER_MAX_SAFE_INTEGER));
    }
}
exports.JsbiSupport = JsbiSupport;
JsbiSupport.ZERO = jsbi_1.default.BigInt(0);
JsbiSupport.ONE = jsbi_1.default.BigInt(1);
JsbiSupport.TWO = jsbi_1.default.BigInt(2);
JsbiSupport.NUMBER_MAX_SAFE_INTEGER = jsbi_1.default.BigInt(Number.MAX_SAFE_INTEGER);
JsbiSupport.NUMBER_MIN_SAFE_INTEGER = jsbi_1.default.BigInt(Number.MIN_SAFE_INTEGER);
